import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { login as loginService, preRegister as preRegisterService, completeRegister as completeRegisterService, refreshAccessToken, getProtectedResourceDetail } from '../services/api';
import axios from 'axios';
import { Dispatch } from 'react';
import { SetStateAction } from 'react';

interface AuthContextProps {
  isAuthenticated: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  preRegister: (email: string, name: string, password: string, re_password: string) => Promise<void>;
  completeRegister: (uid: string, token: string) => Promise<void>;
  logout: () => void;
  isStaff: boolean;
  setIsStaff: Dispatch<SetStateAction<boolean>>;
  isActive: boolean;
  setIsActive: Dispatch<SetStateAction<boolean>>;
  refreshTokenFunction: () => Promise<string | null>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const savedToken = localStorage.getItem('token');
    return savedToken !== null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState<string | null>(() => localStorage.getItem('refreshToken'));
  const [isStaff, setIsStaff] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // 初期化: トークンからユーザー情報を復元
  useEffect(() => {
    const initializeAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response_detail = await getProtectedResourceDetail(savedToken);
          setIsStaff(response_detail.data.is_staff);
          setIsActive(response_detail.data.is_active);
        } catch (error) {
          console.error('Failed to get user details:', error);
          // トークンが無効な場合はログアウト
          logout();
        }
      }
      setIsLoading(false);
    };
    initializeAuth();
  }, []);

  const login = async (username: string, password: string) => {
    const response = await loginService(username, password);
    setToken(response.data.access);
    setRefreshToken(response.data.refresh);
    setIsAuthenticated(true);
    localStorage.setItem('token', response.data.access);
    localStorage.setItem('refreshToken', response.data.refresh);

    const response_detail = await getProtectedResourceDetail(response.data.access)
    await setIsStaff(response_detail.data.is_staff)
    await setIsActive(response_detail.data.is_active)
  };

  const preRegister = async (email: string, name: string, password: string, re_password: string) => {
    await preRegisterService(email, name, password, re_password);
  };

  const completeRegister = async (uid: string, token: string) => {
    await completeRegisterService(uid, token);
  };

  const logout = () => {
    setToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  };

  const refreshTokenFunction = async () => {
    if (!refreshToken) return null;

    try {
      const response = await refreshAccessToken(refreshToken);
      setToken(response.data.access);  // ← 修正
      localStorage.setItem('token', response.data.access);  // ← 修正
      return response.data.access;  // ← 新しいトークンを返す
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      logout();
      return null;
    }
  };

  useEffect(() => {
    const requestInterceptor = (config: any) => {
      if (token) {
        config.headers['Authorization'] = `JWT ${token}`;
      }
      return config;
    };

    const responseInterceptor = async (error: any) => {
      const originalRequest = error.config;
      if (error.response && error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const newToken = await refreshTokenFunction();
        if (newToken) {
          originalRequest.headers['Authorization'] = `JWT ${newToken}`;  // ← 新しいトークンをセット
          return axios(originalRequest);
        }
      }
      return Promise.reject(error);
    };

    const requestInterceptorId = axios.interceptors.request.use(requestInterceptor);
    const responseInterceptorId = axios.interceptors.response.use(response => response, responseInterceptor);

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, [token, refreshToken]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, login, preRegister, completeRegister, logout, isStaff, setIsStaff, isActive, setIsActive, refreshTokenFunction, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
